import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/api-hooks/useAuth';
import AppApi from '@/apis/app-api';
import { ReferralApi } from '@/apis/referral.api';
import UserApi from '@/apis/user.api';
import { baseUrl } from '@/consts/route';
import { ReferralCode, ReferralUse } from '@/types/referral';

export const useReferralCode = () => {
  const { userId, accessToken } = useAuth();

  const getOwnReferralCode = async () => {
    if (!userId || !accessToken) {
      return {} as ReferralCode;
    }
    const appApi = new AppApi(baseUrl);
    appApi.setup(accessToken);
    const userApi = new UserApi(appApi);
    return await userApi.getOwnReferralCode({ userId });
  };

  return useQuery({
    queryKey: ['referral-code', userId],
    queryFn: getOwnReferralCode,
    enabled: !!userId && !!accessToken,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 404) return false;
      return failureCount < 3;
    },
  });
};

export const useReferralCodeMutation = () => {
  const { userId, accessToken } = useAuth();
  const queryClient = useQueryClient();
  const createReferralCode = async () => {
    if (!userId || !accessToken) {
      return {} as ReferralCode;
    }
    const appApi = new AppApi(baseUrl);
    appApi.setup(accessToken);
    const userApi = new UserApi(appApi);
    return await userApi.createReferralCode({ userId });
  };

  return useMutation({
    mutationKey: ['referral-code', userId],
    mutationFn: createReferralCode,
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: ['referral-code', userId],
      });
    },
  });
};

// 해당 API 는 체크 용도로 사용되므로, useMutation 으로 사용
export const useReferralCodeByLink = () => {
  const getReferralCodeByLinkValue = async (link: string) => {
    const appApi = new AppApi(baseUrl);
    const referralApi = new ReferralApi(appApi);
    return await referralApi.getReferralCodeByLinkValue(link);
  };

  return useMutation({
    mutationFn: getReferralCodeByLinkValue,
    mutationKey: ['referral-code'],
  });
};

export const useReferralCodeByLinkQuery = (link: string | null) => {
  return useQuery({
    queryKey: ['referral-code', 'link', link],
    queryFn: () => {
      const appApi = new AppApi(baseUrl);
      const referralApi = new ReferralApi(appApi);
      return referralApi.getReferralCodeByLinkValue(link!);
    },
    enabled: !!link,
    staleTime: Infinity,
    retry: false,
  });
};

// 해당 API 는 체크 용도로 사용되므로, useMutation 으로 사용
export const useReferralCodeByCode = () => {
  const getReferralCodeByCodeValue = async (code: string) => {
    const appApi = new AppApi(baseUrl);
    const referralApi = new ReferralApi(appApi);
    return await referralApi.getReferralCodeByCodeValue(code);
  };

  return useMutation({
    mutationFn: getReferralCodeByCodeValue,
    mutationKey: ['referral-code'],
  });
};

export const useReferralUse = () => {
  const { userId, accessToken } = useAuth();

  const getOwnReferralUse = async () => {
    if (!userId || !accessToken) {
      return {} as ReferralUse;
    }
    const appApi = new AppApi(baseUrl);
    appApi.setup(accessToken);
    const userApi = new UserApi(appApi);
    return await userApi.getOwnReferralUse({ userId });
  };

  return useQuery({
    queryKey: ['referral-use', userId],
    queryFn: getOwnReferralUse,
    enabled: !!userId && !!accessToken,
  });
};
